import {noop, Subject, Subscription} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {Component, EventEmitter, forwardRef, inject, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MessageTranslateService} from '../../services';
import {Equipment, EquipmentId, MetadataTypeId, UtilityTypeMetadataTypeId} from '@app/graphql-typings/graphql';
import {EquipmentCache, EquipmentCacheImpl} from '@shared/data-stores/equipment-cache';
import {ArrayUtils, CollectionUtils} from '@shared/util/collections/collection-utils';
import {I18nMessage} from "@shared/api/model/i18n";
import {objectsEqual} from "@shared/util/comparitors/objects-comparator";

type EquipmentSelectValue = Equipment | Equipment[] | EquipmentId | EquipmentId[] | null;

export interface EquipmentSelectSearchTerm {
  metadataTypeIds?: MetadataTypeId[];
}

@Component({
  selector: 'app-equipment-select',
  templateUrl: './equipment-select.component.html',
  styleUrls: ['./equipment-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => EquipmentSelectComponent)
    },
    {
      // Use an ancestor's cache when there is one, so a page loads a site's equipment once. Otherwise take our own
      provide: EquipmentCache,
      useFactory: () => inject(EquipmentCache, {optional: true, skipSelf: true})
        ?? Injector.create({providers: [{provide: EquipmentCache, useClass: EquipmentCacheImpl}], parent: inject(Injector)})
          .get(EquipmentCache)
    }
  ],
  standalone: false
})
export class EquipmentSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() siteId: number;
  @Input() loading = false;
  @Input() options: Equipment[];
  @Input() optionsSearchTerm: EquipmentSelectSearchTerm | null = null;
  @Input() placeholder: string;
  @Input() hideSelected: boolean;
  @Input() multiSelect = false;
  @Input() bindToEquipmentId = false;
  @Input() appendTo: string;
  @Input() maxSelectedLabels = 3;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage = {messageKey: 'equipment'};
  @Input() truncateLabels = false;
  @Input() groupBy: string | ((equipment: Equipment) => any);
  @Input() hideValueLabelsWhileLoading = false;
  @Input() showItemsWhileLoading = false;
  @Input() isReadOnly = false;
  @Input() customSortFn: ((equipment: Equipment[], secondarySortFn?: (a: Equipment, b: Equipment) => number) => Equipment[]);
  @Input() tooltipById: Map<EquipmentId, I18nMessage> | null = null;
  @Input() clearable = true;
  @Output() openEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();

  readonly DISPLAY_LABEL_NAME = 'display_label';
  readonly EQUIPMENT_ID_KEY = 'equipment_id';

  private equipmentById: Map<EquipmentId, Equipment>;
  /** Holds all equipment available. It is then Filtered by {@link optionsSearchTerm} and assigned to {@link equipment} */
  private allEquipment: Equipment[];

  formControl = new FormControl<EquipmentSelectValue>(null);
  equipment: Equipment[];

  // Mapping between Metadata Type id and the i18n key of the prefix that should be used for the equipment
  private readonly EQUIPMENT_NAME_PREFIXES = new Map<number, string>([
    [UtilityTypeMetadataTypeId.ELECTRICITY, 'Electricity Meter'],
    [UtilityTypeMetadataTypeId.GAS, 'Gas Meter'],
    [UtilityTypeMetadataTypeId.WATER, 'Water Meter']]);
  private readonly ngUnsubscribe = new Subject<void>();
  private equipmentSubscription: Subscription;

  constructor(private equipmentCache: EquipmentCache,
              private translateService: MessageTranslateService) {
  }

  private onChange: (value: EquipmentSelectValue) => void = noop;
  private onTouched: () => void = noop;

  ngOnInit() {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => this.fireOnChange(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.siteId || changes.options) {
      if (this.options == null) {
        this.loadEquipmentBySiteId();
      } else {
        // Options replace what a site fetch would have loaded, so drop one still in flight
        this.equipmentSubscription?.unsubscribe();
        this.processEquipmentRequestResults(this.options);
      }
    } else if (changes.optionsSearchTerm
      && !this.searchTermsEqual(changes.optionsSearchTerm.previousValue, changes.optionsSearchTerm.currentValue)) {
      this.updateEquipmentOptions();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getId(item: Equipment | EquipmentId): EquipmentId {
    // Always returns an EquipmentId for the given item, whether it's an Equipment or EquipmentId.
    return item != null && typeof item === 'object' && this.EQUIPMENT_ID_KEY in item
      ? item.equipment_id
      : (<EquipmentId>item ?? null);
  }

  writeValue(value: EquipmentSelectValue): void {
    let valueChanged: boolean;
    if (this.multiSelect) {
      const arr1: EquipmentId[] = (<Equipment[] | EquipmentId[]>this.formControl.value ?? [])?.map(item => this.getId(item));
      const arr2: EquipmentId[] = (<Equipment[] | EquipmentId[]>value ?? [])?.map(item => this.getId(item));
      valueChanged = !ArrayUtils.containSameItems(arr1, arr2);
    } else {
      valueChanged = this.getId(<Equipment | EquipmentId>this.formControl.value) !== this.getId(<Equipment | EquipmentId>value);
    }
    if (valueChanged) {
      this.formControl.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: EquipmentSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable({emitEvent: false});
    } else {
      this.formControl.enable({emitEvent: false});
    }
  }

  aceSelectTouched(): void {
    this.onTouched();
  };

  onOpen(): void {
    this.openEvent.emit();
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  compareFn(a: Equipment, b: Equipment): boolean {
    return a.equipment_id === b.equipment_id;
  }

  searchFn = (term: string, item: Equipment): boolean => {
    return this.getEquipmentDisplayName(item).toLocaleLowerCase().includes(term.toLocaleLowerCase());
  };

  private getEquipmentDisplayName(equipment: Equipment): string {
    if (equipment) {
      const namePrefix = this.EQUIPMENT_NAME_PREFIXES.get(equipment.metadata_type_id);
      return (namePrefix ? this.translateService.instant(namePrefix) + ' - ' : '') + equipment.name;
    } else {
      return '';
    }
  }

  private fireOnChange(value: EquipmentSelectValue): void {
    this.onChange(value);
  }

  private loadEquipmentBySiteId(): void {
    this.equipmentSubscription?.unsubscribe();
    if (this.siteId != null) {
      this.loading = true;
      const subscription = this.equipmentCache.getEquipmentForSite(this.siteId)
        .pipe(takeUntil(this.ngUnsubscribe), finalize(() => {
          this.loading = false;
          this.equipmentSubscription = null;
        }))
        .subscribe((equipmentArray: Equipment[]) => {
          this.processEquipmentRequestResults(equipmentArray);
        });
      if (!subscription.closed) {
        this.equipmentSubscription = subscription;
      }
    } else {
      this.processEquipmentRequestResults([]);
    }
  }

  private processEquipmentRequestResults(equipmentArray: Equipment[]): void {
    if (equipmentArray?.length) {
      if(equipmentArray.length === 1) {
        this.setEquipmentDisplayName(equipmentArray[0]);
        this.allEquipment = equipmentArray;
      } else {
        this.allEquipment = this.customSortFn ? this.customSortFn(equipmentArray, this.sortEquipmentByDisplayName) : equipmentArray.sort(this.sortEquipmentByDisplayName);
      }
    } else {
      this.allEquipment = equipmentArray;
    }
    this.updateEquipmentOptions();
  }

  private updateEquipmentOptions(): void {
    const matchesSearchTerm = this.buildEquipmentPredicate(this.optionsSearchTerm);
    const equipmentArray = matchesSearchTerm ? this.allEquipment?.filter(matchesSearchTerm) : this.allEquipment;
    this.equipmentById = CollectionUtils.indexBy(equipmentArray, this.EQUIPMENT_ID_KEY);
    this.equipment = equipmentArray;
    this.adjustSelectedEquipment();
  }

  private buildEquipmentPredicate(searchTerm: EquipmentSelectSearchTerm | null): ((equipment: Equipment) => boolean) | null {
    if (!searchTerm?.metadataTypeIds?.length) {
      return null;
    }
    const metadataTypeIds = new Set<MetadataTypeId>(searchTerm.metadataTypeIds);
    return equipment => metadataTypeIds.has(equipment?.metadata_type_id);
  }

  private searchTermsEqual(term1: EquipmentSelectSearchTerm | null, term2: EquipmentSelectSearchTerm | null): boolean {
    return objectsEqual(term1 ?? null, term2 ?? null);
  }

  private sortEquipmentByDisplayName = (a: Equipment, b: Equipment): number => {
    this.setEquipmentDisplayName(a);
    this.setEquipmentDisplayName(b);
    return a[this.DISPLAY_LABEL_NAME].localeCompare(b[this.DISPLAY_LABEL_NAME]);
  };

  private setEquipmentDisplayName(e: Equipment): void {
    if (e[this.DISPLAY_LABEL_NAME] == null) {
      e[this.DISPLAY_LABEL_NAME] = this.getEquipmentDisplayName(e);
    }
  }

  private adjustSelectedEquipment(): void {
    /** Ensures the selected equipment(s) in the form control are valid and synchronized with the available equipment
     * list (equipmentById) and updates the form value if any invalid or unavailable selections are detected */
    const value = this.formControl.value;
    if (value == null) {
      return;
    }

    const selectedValues = Array.isArray(value) ? value : [value];
    const selectedEquipmentIds: EquipmentId[] = selectedValues.map(item => this.getId(item));
    const validEquipment: Equipment[] = [];
    const validEquipmentIds: EquipmentId[] = [];
    selectedEquipmentIds.forEach(id => {
      const equipment = this.equipmentById.get(id);
      if (equipment != null) {
        validEquipmentIds.push(id);
        validEquipment.push(equipment);
      }
    });

    if (!ArrayUtils.containSameItems(selectedEquipmentIds, validEquipmentIds)) {
      const newValueCollection = this.bindToEquipmentId ? validEquipmentIds : validEquipment;
      const newValue = newValueCollection.length
        ? this.multiSelect ? newValueCollection : newValueCollection[0]
        : null;
      this.formControl.setValue(newValue, { emitEvent: false });
      this.fireOnChange(newValue);
    }
  }

  // Must be implemented as an arrow function when passed as an @Input parameter
  // Regular functions lose their parent component's 'this' context when executed in child components.
  getTooltip = (item: Equipment): I18nMessage | null => {
    return item?.equipment_id !== null
      ? this.tooltipById?.get(item.equipment_id) ?? null
      : null;
  };

}
